import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  RefreshCw,
  Calendar,
  ArrowUpCircle,
  ArrowDownCircle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Package,
} from "lucide-react";

export const FoodDeliveryLedger = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("all");
  const [entryType, setEntryType] = useState("all");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Settlement dialog
  const [showSettlementDialog, setShowSettlementDialog] = useState(false);
  const [settlementDriver, setSettlementDriver] = useState(null);
  const [settlementAmount, setSettlementAmount] = useState("");
  const [settlementType, setSettlementType] = useState("driver_to_admin");
  const [settlementNotes, setSettlementNotes] = useState("");
  const [settlementLoading, setSettlementLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [dateFrom, dateTo, selectedDriver, entryType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch summary
      const summaryParams = new URLSearchParams();
      if (dateFrom) summaryParams.append("date_from", dateFrom);
      if (dateTo) summaryParams.append("date_to", dateTo);
      
      const summaryRes = await axios.get(`${API}/quick-commerce/admin/food-delivery-summary?${summaryParams}`);
      setSummary(summaryRes.data);
      
      // Fetch ledger entries
      const ledgerParams = new URLSearchParams({ limit: "100" });
      if (dateFrom) ledgerParams.append("date_from", dateFrom);
      if (dateTo) ledgerParams.append("date_to", dateTo);
      if (selectedDriver !== "all") ledgerParams.append("driver_id", selectedDriver);
      if (entryType !== "all") ledgerParams.append("entry_type", entryType);
      
      const ledgerRes = await axios.get(`${API}/quick-commerce/admin/food-delivery-ledger?${ledgerParams}`);
      setLedgerEntries(ledgerRes.data.entries || []);
    } catch (err) {
      console.error("Failed to fetch food delivery data:", err);
      toast.error("Failed to load food delivery ledger");
    } finally {
      setLoading(false);
    }
  };

  const handleSettlement = async () => {
    if (!settlementDriver || !settlementAmount) {
      toast.error("Please fill all required fields");
      return;
    }
    
    setSettlementLoading(true);
    try {
      await axios.post(`${API}/quick-commerce/admin/food-delivery-settlement`, null, {
        params: {
          driver_id: settlementDriver.driver_id,
          amount: parseFloat(settlementAmount),
          settlement_type: settlementType,
          notes: settlementNotes || undefined
        }
      });
      
      toast.success("Settlement recorded successfully");
      setShowSettlementDialog(false);
      setSettlementAmount("");
      setSettlementNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create settlement");
    } finally {
      setSettlementLoading(false);
    }
  };

  const openSettlementDialog = (rider) => {
    setSettlementDriver(rider);
    setSettlementAmount(Math.abs(rider.balance).toString());
    setSettlementType(rider.balance < 0 ? "driver_to_admin" : "admin_to_driver");
    setShowSettlementDialog(true);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" data-testid="food-delivery-ledger-title">
          Food Delivery Ledger
        </h2>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ArrowUpCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Credits</p>
                <p className="text-xl font-bold text-green-600">₹{summary.summary.total_credits}</p>
                <p className="text-xs text-muted-foreground">Delivery charges</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <ArrowDownCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Debits</p>
                <p className="text-xl font-bold text-red-600">₹{summary.summary.total_debits}</p>
                <p className="text-xs text-muted-foreground">COD collections</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${summary.summary.net_balance >= 0 ? "bg-blue-100" : "bg-amber-100"}`}>
                <DollarSign className={`w-5 h-5 ${summary.summary.net_balance >= 0 ? "text-blue-600" : "text-amber-600"}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Balance</p>
                <p className={`text-xl font-bold ${summary.summary.net_balance >= 0 ? "text-blue-600" : "text-amber-600"}`}>
                  ₹{summary.summary.net_balance}
                </p>
                <p className="text-xs text-muted-foreground">
                  {summary.summary.net_balance >= 0 ? "Owed to riders" : "Owed by riders"}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Deliveries</p>
                <p className="text-xl font-bold">{summary.summary.total_deliveries}</p>
                <p className="text-xs text-muted-foreground">{summary.summary.total_riders} riders</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Riders Balance Table */}
      {summary && summary.all_riders && summary.all_riders.length > 0 && (
        <div className="bg-card border rounded-xl overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Rider Balances
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rider</TableHead>
                <TableHead className="text-right">Deliveries</TableHead>
                <TableHead className="text-right">Credits (Earned)</TableHead>
                <TableHead className="text-right">Debits (COD)</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.all_riders.map((rider) => (
                <TableRow key={rider.driver_id} className={rider.balance < 0 ? "bg-red-50" : rider.balance > 0 ? "bg-green-50" : ""}>
                  <TableCell>
                    <p className="font-medium">{rider.driver_name}</p>
                    {rider.driver_phone && (
                      <p className="text-xs text-muted-foreground">{rider.driver_phone}</p>
                    )}
                    <p className="text-xs text-muted-foreground">Last: {formatDateTime(rider.last_entry)}</p>
                  </TableCell>
                  <TableCell className="text-right">{rider.deliveries}</TableCell>
                  <TableCell className="text-right text-green-600">₹{rider.total_credits}</TableCell>
                  <TableCell className="text-right text-red-600">₹{rider.total_debits}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-bold ${rider.balance < 0 ? "text-red-600" : rider.balance > 0 ? "text-green-600" : ""}`}>
                      ₹{rider.balance}
                    </span>
                    {rider.balance < 0 && (
                      <p className="text-xs text-red-500">Owes admin</p>
                    )}
                    {rider.balance > 0 && (
                      <p className="text-xs text-green-500">Owed by admin</p>
                    )}
                  </TableCell>
                  <TableCell>
                    {rider.balance !== 0 && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openSettlementDialog(rider)}
                      >
                        Settle
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-card border rounded-xl p-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-36"
          />
          <span>to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-36"
          />
        </div>
        
        <Select value={selectedDriver} onValueChange={setSelectedDriver}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="All Riders" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Riders</SelectItem>
            {summary?.all_riders?.map((rider) => (
              <SelectItem key={rider.driver_id} value={rider.driver_id}>
                {rider.driver_name} {rider.driver_phone ? `(${rider.driver_phone})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={entryType} onValueChange={setEntryType}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="credit">Credits Only</SelectItem>
            <SelectItem value="debit">Debits Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Ledger Entries Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Transaction History</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Rider</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ledgerEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              ledgerEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="text-sm">{formatDateTime(entry.created_at)}</TableCell>
                  <TableCell>
                    <p className="font-medium">{entry.driver_name}</p>
                    {entry.driver_phone && (
                      <p className="text-xs text-muted-foreground">{entry.driver_phone}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    {entry.type === "credit" ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <TrendingUp className="w-3 h-3" /> Credit
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <TrendingDown className="w-3 h-3" /> Debit
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs bg-muted px-2 py-1 rounded capitalize">
                      {entry.category?.replace("_", " ")}
                    </span>
                    {entry.is_cdc && (
                      <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">CDC</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {entry.description}
                  </TableCell>
                  <TableCell className={`text-right font-bold ${entry.type === "credit" ? "text-green-600" : "text-red-600"}`}>
                    {entry.type === "credit" ? "+" : "-"}₹{entry.amount}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Settlement Dialog */}
      <Dialog open={showSettlementDialog} onOpenChange={setShowSettlementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Settlement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-muted-foreground">Rider</p>
              <p className="font-medium">{settlementDriver?.driver_name}</p>
              <p className="text-sm">
                Current Balance: 
                <span className={settlementDriver?.balance < 0 ? "text-red-600" : "text-green-600"}>
                  {" "}₹{settlementDriver?.balance}
                </span>
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Settlement Type</label>
              <Select value={settlementType} onValueChange={setSettlementType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver_to_admin">Driver pays Admin</SelectItem>
                  <SelectItem value="admin_to_driver">Admin pays Driver</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Amount</label>
              <Input
                type="number"
                value={settlementAmount}
                onChange={(e) => setSettlementAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Input
                value={settlementNotes}
                onChange={(e) => setSettlementNotes(e.target.value)}
                placeholder="Add notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettlementDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSettlement} disabled={settlementLoading}>
              {settlementLoading ? "Processing..." : "Confirm Settlement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoodDeliveryLedger;
